import { lastReported } from "@/config";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { atom } from "jotai";
import { atomWithCache } from "jotai-cache";
import { loadable } from "jotai/utils";

import {
  areaLevelAtom,
  firstAreaAtom,
  markersAtom,
  secondAreaAtom,
  selectedTimeAtom,
} from "./atoms";

dayjs.extend(customParseFormat);

export const asyncHeatmapQuery = atomWithCache(async (get) => {
  const firstArea = get(firstAreaAtom);
  const secondArea = get(secondAreaAtom);

  const selectedTime = get(selectedTimeAtom);

  const areaLevel = firstArea?.type;

  const startDate = lastReported[selectedTime].start;
  const endDate = lastReported[selectedTime].end;

  let features = [];
  const layer = areaLevel !== "county" ? areaLevel : "district";

  if (firstArea?.id) {
    const res = await fetch(
      `${
        import.meta.env.VITE_API_URL
      }/thefts?startDate=${startDate}&endDate=${endDate}&layerType=${layer}&layerId=${firstArea.id}`,
    );
    const firstAreaThefts = await res.json();
    if (firstAreaThefts.features) features = [...firstAreaThefts.features];
  }
  if (secondArea?.id) {
    const res = await fetch(
      `${
        import.meta.env.VITE_API_URL
      }/thefts/?startDate=${startDate}&endDate=${endDate}&layerType=${layer}&layerId=${secondArea.id}`,
    );
    const secondAreaThefts = await res.json();
    if (secondAreaThefts.features) features = [...features, ...secondAreaThefts.features];
  }
  if (!firstArea?.id && !secondArea?.id) {
    const res = await fetch(
      `${
        import.meta.env.VITE_API_URL
      }/thefts/?startDate=${startDate}&endDate=${endDate}&layerType=country&layerId=82`,
    );
    const response = await res.json();
    features = [...response.features];
  }

  return {
    type: "FeatureCollection",
    features: features,
  };
});

export const heatmapQuery = loadable(asyncHeatmapQuery);

export const affectedAreasQuery = atom(async (get) => {
  const areaLevel = get(areaLevelAtom);
  const selectedTime = get(selectedTimeAtom);

  const date = lastReported[selectedTime].start;
  const layerType = areaLevel !== "county" ? areaLevel : "district";
  const res = await fetch(
    `${import.meta.env.VITE_API_URL}/stats/most-affected/${date}?layerType=${layerType}&limit=4`,
  );
  const areas = await res.json();
  return areas;
});

export const firstAreaMonthlyStatsQuery = atom(async (get) => {
  const areaLevel = get(areaLevelAtom);
  const firstArea = get(firstAreaAtom);
  const selectedTime = get(selectedTimeAtom);

  const id = firstArea?.id;
  const isLastReported = selectedTime === 7;
  const index = (isLastReported ? selectedTime - 1 : selectedTime) as 1 | 2 | 3 | 4 | 5 | 6;
  const startDate = lastReported[index].start;
  const endDate = lastReported[index].end;

  if (id) {
    const layerType = areaLevel !== "county" ? areaLevel : "district";
    const res = await fetch(
      `${
        import.meta.env.VITE_API_URL
      }/stats/monthly/${layerType}?layerId=${id}&start=${startDate}&end=${endDate}`,
    );
    const areas = await res.json();

    return areas;
  }
  return;
});

export const secondAreaMonthlyStatsQuery = atom(async (get) => {
  const areaLevel = get(areaLevelAtom);
  const secondArea = get(secondAreaAtom);
  const selectedTime = get(selectedTimeAtom);
  const id = secondArea?.id;

  const startDate = lastReported[selectedTime].start;
  const endDate = lastReported[selectedTime].end;

  if (id) {
    const layerType = areaLevel !== "county" ? areaLevel : "district";
    const res = await fetch(
      `${
        import.meta.env.VITE_API_URL
      }/stats/monthly/${layerType}?layerId=${id}&start=${startDate}&end=${endDate}`,
    );
    const areas = await res.json();
    return areas;
  }
  return;
});

export const nationalAverageMonthlyStatsQuery = atom(async (get) => {
  const areaLevel = get(areaLevelAtom);
  const selectedTime = get(selectedTimeAtom);

  const startDate = lastReported[selectedTime].start;
  const endDate = lastReported[selectedTime].end;

  const layerType = areaLevel !== "county" ? areaLevel : "district";
  const res = await fetch(
    `${
      import.meta.env.VITE_API_URL
    }/stats/national-average-monthly/${layerType}?start=${startDate}&end=${endDate}`,
  );
  const areas = await res.json();
  return areas;
});

export const firstAreaCardStatsQuery = atom(async (get) => {
  const areaLevel = get(areaLevelAtom);
  const firstArea = get(firstAreaAtom);
  const selectedTime = get(selectedTimeAtom);

  const id = firstArea?.id;
  const name = firstArea?.name;
  const isLastReported = selectedTime === 7;
  const index = (isLastReported ? selectedTime - 1 : selectedTime) as 1 | 2 | 3 | 4 | 5 | 6;
  const startDate = lastReported[index].start;
  const endDate = lastReported[index].end;

  if (id) {
    const layerType = areaLevel !== "county" ? areaLevel : "district";
    const res = await fetch(
      `${
        import.meta.env.VITE_API_URL
      }/stats/${layerType}?layerId=${id}&start=${startDate}&end=${endDate}`,
    );
    const firstAreaStats = await res.json();
    return { name, ...firstAreaStats };
  }
  return null;
});

export const secondAreaCardStatsQuery = atom(async (get) => {
  const areaLevel = get(areaLevelAtom);
  const secondArea = get(secondAreaAtom);
  const selectedTime = get(selectedTimeAtom);

  const id = secondArea?.id;
  const name = secondArea?.name;

  const startDate = lastReported[selectedTime].start;
  const endDate = lastReported[selectedTime].end;

  if (id) {
    const layerType = areaLevel !== "county" ? areaLevel : "district";
    const res = await fetch(
      `${
        import.meta.env.VITE_API_URL
      }/stats/${layerType}?layerId=${id}&start=${startDate}&end=${endDate}`,
    );
    const secondAreaStats = await res.json();
    return { name, ...secondAreaStats };
  }
  return null;
});

export const nationalAverageCardStatsQuery = atom(async (get) => {
  const areaLevel = get(areaLevelAtom);
  const selectedTime = get(selectedTimeAtom);

  const startDate = lastReported[selectedTime].start;
  const endDate = lastReported[selectedTime].end;

  const layerType = areaLevel !== "county" ? areaLevel : "district";
  const res = await fetch(
    `${
      import.meta.env.VITE_API_URL
    }/stats/national-average/${layerType}?&start=${startDate}&end=${endDate}`,
  );
  const nationalStats = await res.json();

  return { name: "National Average", ...nationalStats };
});

export const asyncAreaCardsInfoQuery = atom(async (get) => {
  const selectedTime = get(selectedTimeAtom);
  const hasSearchCards = get(hasSearchCardsQuery);
  const nationalAverage = await get(nationalAverageCardStatsQuery);

  if (hasSearchCards) {
    const markers = get(markersAtom);
    const year = lastReported[selectedTime].start.split("-")[0];

    if (markers)
      return [
        ...markers.map((marker) => ({
          ...marker,
          name: marker?.location,
          total:
            selectedTime === 7
              ? marker?.counts.lastReportedMonthCount
              : marker?.counts.yearCounts[year],
        })),
        nationalAverage,
      ];
    return [];
  }
  if (selectedTime !== 7) {
    const firstArea = await get(firstAreaCardStatsQuery);
    const secondArea = await get(secondAreaCardStatsQuery);
    return [firstArea, secondArea, nationalAverage].filter((el) => el != null);
  }
  const firstAreaMonthlyStats = await get(firstAreaMonthlyStatsQuery);
  const secondAreaMonthlyStats = await get(secondAreaMonthlyStatsQuery);
  const nationalAverageMonthlyStats = await get(nationalAverageMonthlyStatsQuery);

  return [
    {
      name: firstAreaMonthlyStats?.name,
      ...firstAreaMonthlyStats?.monthlyStats[firstAreaMonthlyStats?.monthlyStats.length - 1],
    },
    {
      name: secondAreaMonthlyStats?.name,
      ...secondAreaMonthlyStats?.monthlyStats[secondAreaMonthlyStats?.monthlyStats.length - 1],
    },
    {
      name: nationalAverageMonthlyStats?.name,
      ...nationalAverageMonthlyStats?.monthlyStats[
        nationalAverageMonthlyStats?.monthlyStats.length - 1
      ],
    },
  ].filter((el) => el != null);
});

export const areaCardsInfoQuery = loadable(asyncAreaCardsInfoQuery);

export const asyncDefaultCardQuery = atom(async (get) => {
  const firstAreaMonthlyStats = await get(firstAreaMonthlyStatsQuery);

  return firstAreaMonthlyStats?.monthlyStats.slice(-1)[0];
});
export const defaultCardQuery = loadable(asyncDefaultCardQuery);

export const hasChartDataQuery = atom((get) => {
  const chartData = get(chartDataQuery);

  return chartData.data.length;
});

export const hasDropdownQuery = atom((get) => {
  const firstArea = get(firstAreaAtom);
  const markers = get(markersAtom);
  return firstArea?.name !== "United Kingdom" && !markers.some((el) => el?.isSearch);
});

export const hasSearchCardsQuery = atom((get) => {
  const markers = get(markersAtom);
  return markers.some((el) => el?.isSearch);
});

export const chartDataQuery = atom(async (get) => {
  const firstAreaMonthlyStats = await get(firstAreaMonthlyStatsQuery);
  const secondAreaMonthlyStats = await get(secondAreaMonthlyStatsQuery);
  const nationalAverageMonthlyStats = await get(nationalAverageMonthlyStatsQuery);

  let showNationalAverage = true;
  let dataTemp = [];
  const keys = [];

  showNationalAverage = firstAreaMonthlyStats?.name !== "United Kingdom";

  if (firstAreaMonthlyStats?.monthlyStats.length > 0) {
    dataTemp = firstAreaMonthlyStats.monthlyStats.map((el) => ({
      name: el.month,
      [`${firstAreaMonthlyStats?.name}`]: el.total,
    }));
  }
  keys[0] = firstAreaMonthlyStats?.name;

  if (secondAreaMonthlyStats?.name) {
    showNationalAverage = secondAreaMonthlyStats.name !== "United Kingdom";

    for (let i = 0; i < dataTemp.length; i++) {
      dataTemp[i] = {
        ...dataTemp[i],
        [`${secondAreaMonthlyStats.name}`]: secondAreaMonthlyStats.monthlyStats[i]
          ? secondAreaMonthlyStats.monthlyStats[i].total
          : 0,
      };
    }
    keys[1] = secondAreaMonthlyStats.name;
  }

  if (showNationalAverage) {
    for (let i = 0; i < dataTemp.length; i++) {
      dataTemp[i] = {
        ...dataTemp[i],
        "National Average": nationalAverageMonthlyStats.monthlyStats[i]
          ? nationalAverageMonthlyStats.monthlyStats[i].total
          : 0,
      };
    }
  }

  return { data: dataTemp, keys };
});
